'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { CalenddyLogo } from '@/components/brand/CalenddyLogo';
import {
  Store,
  Sparkles,
  ArrowRight,
  User,
  Mail,
  Lock,
  Phone,
  Globe,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

export default function RegistroPage() {
  const router = useRouter();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEditedManually, setSlugEditedManually] = useState(false);
  const [category, setCategory] = useState('Barbería y Peluquería');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-generación de slug a partir del nombre si no fue editado a mano
  const handleNameChange = (val: string) => {
    setName(val);
    if (!slugEditedManually) {
      const generated = val
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      setSlug(generated);
    }
  };

  const handleSlugChange = (val: string) => {
    setSlugEditedManually(true);
    const cleaned = val
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-');
    setSlug(cleaned);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          slug,
          category,
          phone,
          email,
          password
        })
      });

      const data = await res.json();
      if (data.success) {
        router.push('/dashboard');
        router.refresh();
      } else {
        setError(data.error || 'No se pudo crear la cuenta.');
      }
    } catch (err: any) {
      setError('Error de conexión con el servidor.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7FA] text-[#191923] flex flex-col font-sans selection:bg-[#6255F5] selection:text-white">
      {/* Header */}
      <header className="w-full py-6 px-6 max-w-6xl mx-auto flex items-center justify-between">
        <CalenddyLogo href="/" size="md" />
        <Link
          href="/ingresar"
          className="text-xs font-semibold text-slate-500 hover:text-[#6255F5] transition"
        >
          ¿Ya tenés cuenta? Iniciar sesión →
        </Link>
      </header>

      {/* Main Container */}
      <div className="flex-1 flex items-center justify-center p-4 py-8">
        <div className="max-w-xl w-full bg-white rounded-3xl p-8 sm:p-10 border border-slate-200/90 shadow-xl space-y-6">
          <div className="text-center space-y-2">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#ECEAFF] text-[#6255F5] text-xs font-bold">
              <Sparkles className="w-3.5 h-3.5" />
              Empieza a recibir turnos hoy
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-[#191923] tracking-tight">
              Creá tu negocio en Calenddy
            </h1>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Configurá tu agenda online, conectá tu WhatsApp y compartí tu enlace en 2 minutos.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-xl flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Nombre del Negocio */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Nombre de tu Negocio o Estudio</label>
              <div className="relative">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  required
                  placeholder="Ej: Barbería Norte, Estudio Belén, etc."
                  className="w-full pl-9 pr-3 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#6255F5]/30 focus:border-[#6255F5]"
                />
                <Store className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
              </div>
            </div>

            {/* Dirección Web / Slug */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Tu dirección web personalizada</label>
              <div className="relative flex items-center">
                <span className="pl-3 pr-1 text-xs font-medium text-slate-400 select-none bg-slate-100 py-2.5 rounded-l-xl border-y border-l border-slate-200">
                  calenddy.com/
                </span>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  required
                  placeholder="tu-negocio"
                  className="flex-1 px-3 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-r-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#6255F5]/30 focus:border-[#6255F5] font-mono text-[#6255F5] font-bold"
                />
              </div>
              <p className="text-[10px] text-slate-400">
                Esta es la dirección pública que compartirás con tus clientes para que reserven turnos.
              </p>
            </div>

            {/* Rubro */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Rubro del Negocio</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#6255F5]/30 focus:border-[#6255F5]"
              >
                <option value="Barbería y Peluquería">Barbería y Peluquería</option>
                <option value="Salón de Belleza y Estética">Salón de Belleza y Estética</option>
                <option value="Uñas, Pestañas y Cejas">Uñas, Pestañas y Cejas</option>
                <option value="Consultorio y Salud">Consultorio y Salud</option>
                <option value="Tatuajes y Piercing">Tatuajes y Piercing</option>
                <option value="Fitness, Clases y Deporte">Fitness, Clases y Deporte</option>
                <option value="Otro">Otro rubro de servicios</option>
              </select>
            </div>

            {/* Teléfono WhatsApp */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Teléfono WhatsApp de Contacto</label>
              <div className="relative">
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  placeholder="+54 9 11 1234-5678"
                  className="w-full pl-9 pr-3 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#6255F5]/30 focus:border-[#6255F5]"
                />
                <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Email */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Correo Electrónico</label>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="admin@tunegocio.com"
                    className="w-full pl-9 pr-3 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#6255F5]/30 focus:border-[#6255F5]"
                  />
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                </div>
              </div>

              {/* Contraseña */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Contraseña</label>
                <div className="relative">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Mínimo 6 caracteres"
                    className="w-full pl-9 pr-3 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#6255F5]/30 focus:border-[#6255F5]"
                  />
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                </div>
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 bg-[#6255F5] hover:bg-[#5144E5] text-white font-bold text-xs sm:text-sm rounded-xl transition shadow-lg shadow-[#6255F5]/25 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {loading ? (
                  <span>Creando tu negocio...</span>
                ) : (
                  <>
                    <span>Crear cuenta y comenzar gratis</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="pt-2 text-center text-xs text-slate-500 border-t border-slate-100">
            Al registrarte aceptás los Términos de Servicio y la Política de Privacidad de Calenddy.
          </div>
        </div>
      </div>

      <footer className="py-6 text-center text-xs text-slate-400">
        © 2026 Calenddy · Plataforma SaaS de Gestión y Reservas.
      </footer>
    </div>
  );
}
